import { useCallback, useEffect, useRef, useState } from 'react';

import IconButton from 'tailwind/components/IconButton/IconButton';
import UtilityStrokeArrowUpMediumIcon from 'tailwind/components/Icons/UtilityStrokeArrowUpMediumIcon';
import useMartyContext from 'hooks/useMartyContext';

const SCROLL_THRESHOLD = 200;

const ScrollToTopButton = () => {
  const { testId } = useMartyContext();
  const [isVisible, setIsVisible] = useState(false);
  const rafId = useRef<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > SCROLL_THRESHOLD);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    // Cancel any in-progress scroll animation
    if (rafId.current) {
      cancelAnimationFrame(rafId.current);
    }

    const step = () => {
      const currentTop = window.scrollY;
      if (currentTop <= 1) {
        window.scrollTo(0, 0);
        rafId.current = null;
        return;
      }

      const distance = Math.max(currentTop / 10, 1);
      window.scrollTo(0, Math.max(currentTop - distance, 0));
      rafId.current = requestAnimationFrame(step);
    };

    rafId.current = requestAnimationFrame(step);
  }, []);

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
    },
    []
  );

  return (
    <IconButton
      variant="filled"
      size="base"
      label="scroll to top"
      onClick={scrollToTop}
      data-test-id={testId('scrollToTop')}
      className={`!fixed right-6 z-[4] shadow-md transition-[bottom] duration-300 ease-in-out ${isVisible ? 'bottom-8 md:bottom-8' : '-bottom-20'} `}
    >
      <UtilityStrokeArrowUpMediumIcon size={24} />
    </IconButton>
  );
};

export default ScrollToTopButton;
